import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import ErrorBoundary from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import Sidebar from './components/Sidebar'
import Dashboard from './pages/Dashboard'
import Payments from './pages/Payments'
import Analytics from './pages/Analytics'
import Settings from './pages/Settings'
import Documentation from './pages/Documentation'
import AIAssistant from './pages/AIAssistant'
import Monitoring from './pages/Monitoring'
import Testing from './pages/Testing'
import APIPlayground from './pages/APIPlayground'
import WebhookManager from './pages/WebhookManager'
import SecurityCenter from './pages/SecurityCenter'
import AdminDashboard from './pages/AdminDashboard'
import DeveloperDashboard from './pages/DeveloperDashboard'
import MerchantDashboard from './pages/MerchantDashboard'
import AssistantInterface from './components/AssistantInterface'
import NotificationCenter from './components/NotificationCenter'
import ConnectionStatus from './components/ConnectionStatus'
import BoltBadge from './components/BoltBadge'
import LoginPage from './pages/LoginPage'

const AppContent = () => {
  const { user, profile, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de PayFuse...</p>
        </div>
      </div>
    )
  }

  if (!user || !profile) {
    return <LoginPage />
  }

  // Redirection basée sur le rôle
  const getDashboardRoute = () => {
    switch (profile.role) {
      case 'admin':
        return '/admin'
      case 'developer':
        return '/developer'
      case 'merchant':
        return '/merchant'
      default:
        return '/dashboard'
    }
  }

  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 overflow-hidden bg-gray-50">
          {/* Header avec statut de connexion */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900">
                PayFuse Dashboard - {profile.role === 'admin' ? 'Administrateur' : 
                                   profile.role === 'developer' ? 'Développeur' : 'Commerçant'}
              </h2>
              <ConnectionStatus />
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{profile.name}</div>
                <div className="text-xs text-gray-500">{profile.email}</div>
              </div>
              <NotificationCenter />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="h-full overflow-auto"
            >
              <Routes>
                {/* Redirection par défaut basée sur le rôle */}
                <Route path="/" element={<Navigate to={getDashboardRoute()} replace />} />
                
                {/* Routes protégées par rôle */}
                <Route path="/admin" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/developer" element={
                  <ProtectedRoute allowedRoles={['developer']}>
                    <DeveloperDashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/merchant" element={
                  <ProtectedRoute allowedRoles={['merchant']}>
                    <MerchantDashboard />
                  </ProtectedRoute>
                } />
                
                {/* Routes communes protégées */}
                <Route path="/dashboard" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer', 'merchant']}>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/payments" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer', 'merchant']}>
                    <Payments />
                  </ProtectedRoute>
                } />
                
                <Route path="/analytics" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer', 'merchant']}>
                    <Analytics />
                  </ProtectedRoute>
                } />
                
                <Route path="/monitoring" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Monitoring />
                  </ProtectedRoute>
                } />
                
                <Route path="/testing" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer']}>
                    <Testing />
                  </ProtectedRoute>
                } />
                
                <Route path="/playground" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer']}>
                    <APIPlayground />
                  </ProtectedRoute>
                } />
                
                <Route path="/webhooks" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer', 'merchant']}>
                    <WebhookManager />
                  </ProtectedRoute>
                } />
                
                <Route path="/security" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <SecurityCenter />
                  </ProtectedRoute>
                } />
                
                <Route path="/docs" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer', 'merchant']}>
                    <Documentation />
                  </ProtectedRoute>
                } />
                
                <Route path="/ai-assistant" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer']}>
                    <AIAssistant />
                  </ProtectedRoute>
                } />
                
                <Route path="/settings" element={
                  <ProtectedRoute allowedRoles={['admin', 'developer', 'merchant']}>
                    <Settings />
                  </ProtectedRoute>
                } />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </main>
        
        {/* Interface d'assistance */}
        <AssistantInterface />
        
        {/* Badge Bolt.new */}
        <div className="fixed bottom-6 right-6 z-40 flex items-center space-x-3">
          <BoltBadge variant="dark" position="bottom-right" size="md" />
        </div>
      </div>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App